import React from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, MessageSquare } from 'lucide-react';

// Strip everything except digits and leading +. The tel:/sms: URI schemes
// only accept digit characters (E.164 style). All client-side: no backend
// call, no logging — RGPD posture identical to copy-pasting the number
// manually into a softphone or mobile dialer.
export function cleanPhoneForUri(phone) {
  if (!phone) return '';
  const s = String(phone).trim();
  const plus = s.startsWith('+') ? '+' : '';
  return plus + s.replace(/\D/g, '');
}

function PhoneAnchor({ href, ariaLabel, className, children }) {
  return (
    <a
      href={href}
      aria-label={ariaLabel}
      className={className}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  );
}

/**
 * Click-to-call link.
 *
 * variant:
 *   "link"   (default) — inline anchor, phone number visible
 *   "button" — CTA button shape, custom label
 *   "icon"   — icon only, compact for dense rows
 */
export default function PhoneLink({
  phone,
  label,
  variant = 'link',
  className = '',
  showIcon = true,
  ariaLabel,
}) {
  const { t } = useTranslation();
  if (!phone) return null;
  const cleaned = cleanPhoneForUri(phone);
  if (!cleaned) return null;

  const finalLabel = label ?? t('common.entity.phone.call');
  const href = `tel:${cleaned}`;
  const finalAria = ariaLabel || t('common.entity.phone.callAriaLabel', { phone });

  if (variant === 'icon') {
    return (
      <PhoneAnchor
        href={href}
        ariaLabel={finalAria}
        className={`inline-flex items-center justify-center text-blue-600 hover:text-blue-700 ${className}`}
      >
        <Phone className="h-4 w-4" />
      </PhoneAnchor>
    );
  }

  if (variant === 'button') {
    return (
      <PhoneAnchor
        href={href}
        ariaLabel={finalAria}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors ${className}`}
      >
        {showIcon && <Phone className="h-3.5 w-3.5" />}
        <span>{finalLabel}</span>
      </PhoneAnchor>
    );
  }

  return (
    <PhoneAnchor
      href={href}
      ariaLabel={finalAria}
      className={`inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 hover:underline ${className}`}
    >
      {showIcon && <Phone className="h-4 w-4 text-gray-400" />}
      <span>{phone}</span>
    </PhoneAnchor>
  );
}

/**
 * Click-to-SMS link. Optional pre-filled body via the body= URI param.
 * Same client-side handoff as PhoneLink — OS opens the native SMS app.
 */
export function MessageLink({
  phone,
  body = '',
  label,
  variant = 'link',
  className = '',
  showIcon = true,
  ariaLabel,
}) {
  const { t } = useTranslation();
  if (!phone) return null;
  const cleaned = cleanPhoneForUri(phone);
  if (!cleaned) return null;

  const finalLabel = label ?? t('common.entity.phone.sms');
  const bodyParam = body ? `?body=${encodeURIComponent(body)}` : '';
  const href = `sms:${cleaned}${bodyParam}`;
  const finalAria = ariaLabel || t('common.entity.phone.smsAriaLabel', { phone });

  if (variant === 'icon') {
    return (
      <PhoneAnchor
        href={href}
        ariaLabel={finalAria}
        className={`inline-flex items-center justify-center text-emerald-600 hover:text-emerald-700 ${className}`}
      >
        <MessageSquare className="h-4 w-4" />
      </PhoneAnchor>
    );
  }

  if (variant === 'button') {
    return (
      <PhoneAnchor
        href={href}
        ariaLabel={finalAria}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors ${className}`}
      >
        {showIcon && <MessageSquare className="h-3.5 w-3.5" />}
        <span>{finalLabel}</span>
      </PhoneAnchor>
    );
  }

  return (
    <PhoneAnchor
      href={href}
      ariaLabel={finalAria}
      className={`inline-flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700 hover:underline ${className}`}
    >
      {showIcon && <MessageSquare className="h-4 w-4 text-gray-400" />}
      <span>{finalLabel}</span>
    </PhoneAnchor>
  );
}
